import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { firstValueFrom, interval, Subscription } from 'rxjs';
import { AppState } from 'src/app/app.reducer';
import {
  ParametrosEnum,
  PAGO_MOVIL_MONTO_MAX_DEFAULT,
} from 'src/app/shared/enums/parameters.enum';
import { SpinnerService } from 'src/app/shared/services/spinner.service';
import Swal from 'sweetalert2';
import { CuentaBeneficiario } from '../../interfaces/cuenta-beneficiario.interface';
import { CuentaRetiro } from '../../interfaces/cuenta-retiro.interface';
import { AccountRecipientService } from '../../services/account-recipient.service';
import { CartolaService } from '../../services/cartola.service';
import { HoursOperationService } from '../../services/hours-operation.service';
import { MainRatesService } from '../../services/main-rates.service';
import { ParameterService } from '../../services/parameter.service';
import { TransaccionService } from '../../services/transaccion.service';
import { WalletVesClientService } from '../../services/wallet-ves-client.service';
import { WithdrawAccountService } from '../../services/withdraw-account.service';
import AddAccountComponent from '../account-recipient/components/add-account/add-account.component';
import { AddWithdrawAccountsComponent } from '../add-withdraw-accounts/add-withdraw-accounts.component';

interface Currency {
  code: string;
  symbol: string;
  flag: string;
}

@Component({
  selector: 'app-fiat-calculator',
  templateUrl: './fiat-calculator.component.html',
  styleUrls: ['./fiat-calculator.component.sass'],
})
export class FiatCalculatorComponent implements OnInit, OnDestroy {
  currentStep: number = 1;
  totalSteps: number = 4;

  subscription: Subscription = new Subscription();
  ratePollingSubscription?: Subscription;
  user: any;

  currentBalance: number = 0;
  overdraftAvailable: number = 0;

  isCalculatingFromSend: boolean = true;
  currentRange: string = '';
  isCalculatingRate: boolean = false;

  currencies: Currency[] = [
    { code: 'CLP', symbol: '$', flag: 'chile.png' },
    { code: 'VES', symbol: 'Bs', flag: 'venezuela.png' },
  ];

  selectedSendCurrency: Currency = this.currencies[0];
  selectedReceiveCurrency: Currency = this.currencies[1];

  // VE user mode
  tipoId: string = '';
  isVeUser: boolean = false;
  vesBeneficiaries: CuentaRetiro[] = [];
  selectedVesBeneficiary: CuentaRetiro | null = null;

  beneficiaries: CuentaBeneficiario[] = [];
  selectedBeneficiary: CuentaBeneficiario | null = null;
  searchTerm: string = '';

  sendAmount: number = 10000;
  receiveAmount: number = 0;
  transactionCreatedAt: Date | null = null;

  exchangeRate: number = 0;
  confirmedExchangeRate: number = 0;
  tasaCambioId: number = 0;
  valorCambioId: number = 0;
  transferFee: number = 0;
  amountToConvert: number = 0;
  exchangeType: string = '1CLP=0VES';
  minSendAmount: number = 10000;
  maxSendAmount: number = 5000000;

  estimatedArrival: number = 2;

  estadoOperaciones: boolean = false;
  estadoStopOperaciones: boolean = false;

  showRatesModal: boolean = false;
  tasasCambio: any = null;

  pagoMovilGlobal: number = 0;
  pagoMovilBanesco: number = 0;
  pagoMovilDisabled: boolean = true;
  // Límite máximo (en Bs) para Pago Móvil, configurable desde admin (PAGO_MOVIL_MONTO_MAX).
  pagoMovilMontoMax: number = PAGO_MOVIL_MONTO_MAX_DEFAULT;

  transferForm: FormGroup;
  isSubmitting: boolean = false;

  private rateDebounceTimer: any;

  constructor(
    private fb: FormBuilder,
    private hourOperations: HoursOperationService,
    private router: Router,
    private store: Store<AppState>,
    private cartolaService: CartolaService,
    private spinner: SpinnerService,
    private accountRecipientService: AccountRecipientService,
    private dialog: MatDialog,
    private mainRatesService: MainRatesService,
    private transaccionService: TransaccionService,
    private parameterService: ParameterService,
    private walletVesClientService: WalletVesClientService,
    private withdrawAccountService: WithdrawAccountService,
  ) {
    this.transferForm = this.fb.group({
      sendAmount: [
        this.sendAmount,
        [
          Validators.required,
          Validators.min(this.minSendAmount),
          Validators.max(this.maxSendAmount),
        ],
      ],
      receiveAmount: [
        this.receiveAmount,
        [Validators.required, Validators.min(0.01)],
      ],
      selectedBeneficiary: [null, Validators.required],
    });

    this.subscription.add(
      this.store.select('auth').subscribe(({ user }: any) => {
        this.user = user;
        this.tipoId = user?.tipoId || '';
        this.isVeUser = this.tipoId === 'VE';

        if (this.isVeUser) {
          this.selectedSendCurrency = this.currencies[1]; // VES
          this.selectedReceiveCurrency = this.currencies[0]; // CLP
        }
      }),
    );
  }

  async ngOnInit(): Promise<void> {
    await this.validarEstado();

    const spinnerRef = this.spinner.start();

    try {
      // Para CL: pago móvil params debe cargar antes de beneficiarios (se usa en filtro)
      if (!this.isVeUser) {
        await this.obtenerParametrosPagoMovil();
      }

      // Cargar todo en paralelo: saldo, beneficiarios, tasas, modal tasas
      const tasks: Promise<any>[] = [];

      if (this.user?.usuarioId) {
        tasks.push(this.obtenerSaldo());

        if (this.isVeUser) {
          tasks.push(this.obtenerBeneficiariosCl());
        } else {
          tasks.push(this.obtenerBeneficiarios());
        }
      }

      if (this.isVeUser) {
        tasks.push(this.obtenerTasaCambioInicial());
      } else {
        this.currentRange = this.getMontoRange(this.sendAmount);
        tasks.push(this.obtenerTasaCambio(this.sendAmount));
      }

      tasks.push(this.cargarTasasCambio());

      await Promise.all(tasks);
    } finally {
      this.spinner.stop(spinnerRef);
    }

    this.startRatePolling();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    if (this.ratePollingSubscription) {
      this.ratePollingSubscription.unsubscribe();
    }
    if (this.rateDebounceTimer) {
      clearTimeout(this.rateDebounceTimer);
    }
  }

  startRatePolling(): void {
    this.ratePollingSubscription = interval(20000).subscribe(async () => {
      if (this.sendAmount >= this.minSendAmount) {
        await this.obtenerTasaCambio(this.sendAmount, true);
      }
    });
  }

  getMontoRange(monto: number): string {
    // Para VE: convertir VES a CLP equivalente para determinar rango correcto
    const montoClp = this.isVeUser && this.exchangeRate > 0
      ? Math.round(monto / this.exchangeRate)
      : monto;

    if (montoClp >= 10000 && montoClp <= 99999) {
      return '10000-99999';
    } else if (montoClp >= 100000 && montoClp <= 209999) {
      return '100000-209999';
    } else if (montoClp >= 210000 && montoClp <= 5000000) {
      return '210000-5000000';
    }
    return '';
  }

  async nextStep(): Promise<void> {
    if (this.currentStep < this.totalSteps) {
      if (this.validateCurrentStep()) {
        if (this.currentStep === 1) {
          this.confirmedExchangeRate = this.exchangeRate;
        }

        if (this.currentStep === 3) {
          this.isSubmitting = true;
          await this.validateAndConfirmTransfer();
          return;
        }

        this.currentStep++;
      }
    }
  }

  previousStep(): void {
    if (this.currentStep > 1) {
      this.isSubmitting = false;
      this.currentStep--;
    }
  }

  goToStep(step: number): void {
    if (step <= this.totalSteps && step >= 1) {
      this.currentStep = step;
    }
  }

  validateCurrentStep(): boolean {
    switch (this.currentStep) {
      case 1:
        // Si está calculando la tasa, no permitir continuar
        if (this.isCalculatingRate) {
          return false;
        }

        // Validar que los controles del formulario sean válidos
        const sendAmountControl = this.transferForm.get('sendAmount');
        const receiveAmountControl = this.transferForm.get('receiveAmount');

        // Si algún control es inválido, retornar false
        if (!sendAmountControl?.valid || !receiveAmountControl?.valid) {
          return false;
        }

        // Validar que los valores sean números válidos (no NaN, no Infinity)
        if (
          typeof this.sendAmount !== 'number' ||
          isNaN(this.sendAmount) ||
          !isFinite(this.sendAmount) ||
          typeof this.receiveAmount !== 'number' ||
          isNaN(this.receiveAmount) ||
          !isFinite(this.receiveAmount) ||
          typeof this.exchangeRate !== 'number' ||
          isNaN(this.exchangeRate) ||
          !isFinite(this.exchangeRate)
        ) {
          return false;
        }

        // Redondear receiveAmount a 2 decimales para la validación
        const receiveAmountRounded = Math.round(this.receiveAmount * 100) / 100;

        // Validar rangos de sendAmount
        const sendAmountValid =
          this.sendAmount >= this.minSendAmount &&
          this.sendAmount <= this.maxSendAmount &&
          this.sendAmount <= this.currentBalance;

        // Validar que receiveAmount sea positivo
        const receiveAmountValid = receiveAmountRounded > 0;

        // Validar que exchangeRate sea positivo
        const exchangeRateValid = this.exchangeRate > 0;

        // Calcular rangos de receiveAmount y redondear a 2 decimales
        const minReceiveAmount =
          this.exchangeRate > 0
            ? Math.round(
                (this.minSendAmount - this.transferFee) *
                  this.exchangeRate *
                  100,
              ) / 100
            : 0;

        const maxReceiveAmount =
          this.exchangeRate > 0
            ? Math.round(
                (this.maxSendAmount - this.transferFee) *
                  this.exchangeRate *
                  100,
              ) / 100
            : 0;

        // Validar que receiveAmount esté en el rango válido
        const receiveAmountInRange =
          receiveAmountRounded >= minReceiveAmount &&
          receiveAmountRounded <= maxReceiveAmount;

        return (
          sendAmountValid &&
          receiveAmountValid &&
          exchangeRateValid &&
          receiveAmountInRange
        );
      case 2:
        if (this.isVeUser) {
          return this.selectedVesBeneficiary !== null;
        }
        // No permitir avanzar con un beneficiario Pago Móvil si el monto supera el límite
        if (
          this.selectedBeneficiary &&
          this.isBeneficiaryPagoMovilBlocked(this.selectedBeneficiary)
        ) {
          return false;
        }
        return this.selectedBeneficiary !== null;
      case 3:
        return true;
      case 4:
        return true;
      default:
        return false;
    }
  }

  async onSendAmountChange(): Promise<void> {
    this.isCalculatingFromSend = true;

    // Leer el valor del formulario
    const formValue = this.transferForm.get('sendAmount')?.value;
    let inputAmount =
      formValue !== null && formValue !== undefined ? Number(formValue) : 0;

    // Validar que sea un número válido
    if (isNaN(inputAmount) || !isFinite(inputAmount)) {
      this.receiveAmount = 0;
      this.amountToConvert = 0;
      this.transferForm.get('receiveAmount')?.setValue(0, { emitEvent: false });
      this.isCalculatingRate = false;
      return;
    }

    // Redondear a número entero
    this.sendAmount = Math.round(inputAmount);

    // Actualizar el formulario solo si el valor cambió (evitar ciclos)
    if (inputAmount !== this.sendAmount) {
      this.transferForm
        .get('sendAmount')
        ?.setValue(this.sendAmount, { emitEvent: false });
    }

    // Solo calcular si está dentro de rango válido
    if (
      this.sendAmount >= this.minSendAmount &&
      this.sendAmount <= this.maxSendAmount
    ) {
      // Recalcular inmediatamente con la tasa actual (UI responsivo)
      this.calculateReceiveAmount();

      // Debounce: consultar API solo cuando el usuario deja de escribir
      if (this.rateDebounceTimer) {
        clearTimeout(this.rateDebounceTimer);
      }

      const newRange = this.getMontoRange(this.sendAmount);

      if (newRange !== this.currentRange || this.exchangeRate === 0) {
        this.isCalculatingRate = true;
        this.rateDebounceTimer = setTimeout(async () => {
          this.currentRange = newRange;
          await this.obtenerTasaCambio(this.sendAmount, true);
          this.calculateReceiveAmount();
          this.isCalculatingRate = false;
        }, 500);
      } else {
        this.isCalculatingRate = false;
      }
    } else {
      // Si está fuera de rango, resetear receiveAmount
      this.receiveAmount = 0;
      this.amountToConvert = 0;
      this.transferForm.get('receiveAmount')?.setValue(0, { emitEvent: false });
      this.isCalculatingRate = false;
    }
  }

  async onReceiveAmountChange(): Promise<void> {
    this.isCalculatingFromSend = false;

    // Leer el valor del formulario y convertir coma a punto
    let formValue = this.transferForm.get('receiveAmount')?.value;

    // Si el valor es string y tiene coma, reemplazarla por punto
    if (typeof formValue === 'string' && formValue.includes(',')) {
      formValue = formValue.replace(',', '.');
    }

    let inputAmount =
      formValue !== null && formValue !== undefined ? Number(formValue) : 0;

    // Validar que sea un número válido
    if (isNaN(inputAmount) || !isFinite(inputAmount)) {
      this.sendAmount = this.minSendAmount;
      this.amountToConvert = 0;
      this.transferForm
        .get('sendAmount')
        ?.setValue(this.minSendAmount, { emitEvent: false });
      this.isCalculatingRate = false;
      return;
    }

    // Si el usuario escribió un número entero, mantenerlo como entero
    // Si escribió con decimales, redondear a 2 decimales máximo
    if (Number.isInteger(inputAmount)) {
      this.receiveAmount = inputAmount;
    } else {
      this.receiveAmount = Math.round(inputAmount * 100) / 100;
    }

    // Actualizar el formulario solo si el valor cambió (evitar ciclos)
    if (inputAmount !== this.receiveAmount) {
      this.transferForm
        .get('receiveAmount')
        ?.setValue(this.receiveAmount, { emitEvent: false });
    }

    // Si el nuevo monto supera el límite, deseleccionar beneficiario Pago Móvil
    this.clearBlockedPagoMovilSelection();

    // Validar que exchangeRate sea válido antes de usarlo
    if (
      this.receiveAmount > 0 &&
      this.exchangeRate > 0 &&
      isFinite(this.exchangeRate) &&
      !isNaN(this.exchangeRate)
    ) {
      // Calcular el monto a enviar basado en el monto a recibir
      let calculatedSendAmount: number;
      if (this.isVeUser) {
        // Recibe CLP → cuántos VES enviar: VES = CLP * tasa
        calculatedSendAmount =
          this.receiveAmount * this.exchangeRate + this.transferFee;
      } else {
        // Recibe VES → cuántos CLP enviar: CLP = VES / tasa
        calculatedSendAmount =
          this.receiveAmount / this.exchangeRate + this.transferFee;
      }

      // Validar que el resultado sea un número válido
      if (isNaN(calculatedSendAmount) || !isFinite(calculatedSendAmount)) {
        this.sendAmount = this.minSendAmount;
        this.amountToConvert = 0;
        this.transferForm
          .get('sendAmount')
          ?.setValue(this.minSendAmount, { emitEvent: false });
        this.isCalculatingRate = false;
        return;
      }

      // Redondear a número entero
      this.sendAmount = Math.round(calculatedSendAmount);
      this.transferForm
        .get('sendAmount')
        ?.setValue(this.sendAmount, { emitEvent: false });

      // Calcular el monto después de la comisión
      const amountAfterFee = this.sendAmount - this.transferFee;
      this.amountToConvert = Math.max(0, amountAfterFee);

      // Solo obtener nueva tasa si el sendAmount está dentro de rango válido
      if (
        this.sendAmount >= this.minSendAmount &&
        this.sendAmount <= this.maxSendAmount
      ) {
        // Recalcular inmediatamente con tasa actual
        this.calculateSendAmountFromReceive();

        // Debounce: consultar API solo cuando el usuario deja de escribir
        if (this.rateDebounceTimer) {
          clearTimeout(this.rateDebounceTimer);
        }

        const newRange = this.getMontoRange(this.sendAmount);

        if (newRange !== this.currentRange) {
          this.isCalculatingRate = true;
          this.rateDebounceTimer = setTimeout(async () => {
            this.currentRange = newRange;
            await this.obtenerTasaCambio(this.sendAmount, true);
            this.calculateSendAmountFromReceive();
            this.isCalculatingRate = false;
          }, 500);
        } else {
          this.isCalculatingRate = false;
        }
      } else {
        this.isCalculatingRate = false;
      }
    } else {
      this.sendAmount = this.minSendAmount;
      this.amountToConvert = 0;
      this.transferForm
        .get('sendAmount')
        ?.setValue(this.minSendAmount, { emitEvent: false });
      this.isCalculatingRate = false;
    }
  }

  calculateReceiveAmount(): void {
    // Validar que los valores sean números válidos
    if (
      typeof this.sendAmount !== 'number' ||
      isNaN(this.sendAmount) ||
      !isFinite(this.sendAmount) ||
      typeof this.exchangeRate !== 'number' ||
      isNaN(this.exchangeRate) ||
      !isFinite(this.exchangeRate)
    ) {
      this.receiveAmount = 0;
      this.amountToConvert = 0;
      this.transferForm.get('receiveAmount')?.setValue(0, { emitEvent: false });
      return;
    }

    if (this.sendAmount > 0 && this.exchangeRate > 0) {
      const amountAfterFee = this.sendAmount - this.transferFee;

      // Asegurar que amountAfterFee no sea negativo
      if (amountAfterFee <= 0) {
        this.receiveAmount = 0;
        this.amountToConvert = 0;
      } else {
        if (this.isVeUser) {
          // VES→CLP: montoCLP = montoVES / tasaAplicada
          this.receiveAmount =
            Math.round((amountAfterFee / this.exchangeRate) * 100) / 100;
        } else {
          // CLP→VES: montoVES = montoCLP * tasa
          this.receiveAmount =
            Math.round(amountAfterFee * this.exchangeRate * 100) / 100;
        }
        this.amountToConvert = amountAfterFee;
      }
    } else {
      this.receiveAmount = 0;
      this.amountToConvert = 0;
    }

    this.transferForm
      .get('receiveAmount')
      ?.setValue(this.receiveAmount, { emitEvent: false });

    this.clearBlockedPagoMovilSelection();
  }

  calculateSendAmountFromReceive(): void {
    // Calcular CLP desde VES manteniendo el valor VES
    // Esta función se usa cuando la tasa cambia y el usuario estaba editando VES
    if (
      typeof this.receiveAmount !== 'number' ||
      isNaN(this.receiveAmount) ||
      !isFinite(this.receiveAmount) ||
      typeof this.exchangeRate !== 'number' ||
      isNaN(this.exchangeRate) ||
      !isFinite(this.exchangeRate) ||
      this.exchangeRate <= 0
    ) {
      return;
    }

    if (this.receiveAmount > 0) {
      let calculatedSendAmount: number;

      if (this.isVeUser) {
        // CLP recibido → cuántos VES se necesitan: montoVES = montoCLP * tasa
        calculatedSendAmount =
          this.receiveAmount * this.exchangeRate + this.transferFee;
      } else {
        // VES recibido → cuántos CLP se necesitan: montoCLP = montoVES / tasa
        calculatedSendAmount =
          this.receiveAmount / this.exchangeRate + this.transferFee;
      }

      if (isNaN(calculatedSendAmount) || !isFinite(calculatedSendAmount)) {
        return;
      }

      this.sendAmount = Math.round(calculatedSendAmount);
      this.transferForm
        .get('sendAmount')
        ?.setValue(this.sendAmount, { emitEvent: false });

      const amountAfterFee = this.sendAmount - this.transferFee;
      this.amountToConvert = Math.max(0, amountAfterFee);
    }
  }

  calculateTransfer(): void {
    this.calculateReceiveAmount();
  }

  swapCurrencies(): void {
    const temp = this.selectedSendCurrency;
    this.selectedSendCurrency = this.selectedReceiveCurrency;
    this.selectedReceiveCurrency = temp;
    this.calculateTransfer();
  }

  selectBeneficiary(beneficiario: CuentaBeneficiario): void {
    // Al intentar seleccionar un Pago Móvil con monto sobre el límite: avisar y no seleccionar
    if (this.isBeneficiaryPagoMovilBlocked(beneficiario)) {
      Swal.fire({
        confirmButtonColor: '#1e5a3f',
        icon: 'warning',
        title: 'Pago Móvil no disponible',
        text: `Pago Móvil no está disponible para montos superiores a ${this.formatAmountWithThousands(
          this.pagoMovilMontoMax,
        )} Bs. Selecciona un beneficiario de otro tipo.`,
        confirmButtonText: 'Entendido',
      });
      return;
    }
    this.selectedBeneficiary = beneficiario;
    this.transferForm.get('selectedBeneficiary')?.setValue(beneficiario);
  }

  get filteredBeneficiaries(): CuentaBeneficiario[] {
    if (!this.searchTerm) {
      return this.beneficiaries;
    }
    return this.beneficiaries.filter(
      (b) =>
        b.nombre.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
        b.apellidos.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
        b.banco?.nombre.toLowerCase().includes(this.searchTerm.toLowerCase()),
    );
  }

  openAddBeneficiaryDialog(): void {
    const isMobile = window.innerWidth < 768;
    const dialogRef = this.dialog.open(AddAccountComponent, {
      data: {
        type: 'CREATE',
        cuenta: null,
        isVeUser: this.isVeUser,
        // Monto en Bs de la operación: el beneficiario recibe VES (receiveAmount)
        montoBs: this.receiveAmount,
      },
      disableClose: true,
      panelClass: 'aguacate-dialog',
      maxWidth: isMobile ? '95vw' : '620px',
      minWidth: isMobile ? '90vw' : '560px',
      maxHeight: '90vh',
    });

    dialogRef.afterClosed().subscribe(async (result) => {
      if (result === true && this.user?.usuarioId) {
        await this.obtenerBeneficiarios();
      }
    });
  }

  async validateAndConfirmTransfer(): Promise<void> {
    let spinnerRef = this.spinner.start();

    try {
      let currentRate: number;
      let rateId: number;

      if (this.isVeUser) {
        const response = await firstValueFrom(
          this.mainRatesService.obtenerTasaVesClpPorMonto(this.sendAmount, 'Client'),
        );
        currentRate = response.tasaAplicada;
        rateId = response.id;
      } else {
        const response = await firstValueFrom(
          this.mainRatesService.obtenerTasaPorMonto(this.sendAmount, 'Client'),
        );
        currentRate = response.tasaClient;
        rateId = response.id;
      }

      if (currentRate !== this.confirmedExchangeRate) {
        this.spinner.stop(spinnerRef);

        await Swal.fire({
          confirmButtonColor: '#1e5a3f',
          icon: 'warning',
          title: 'Tasa de cambio actualizada',
          text: 'La tasa de cambio ha cambiado. Por favor, revisa los nuevos montos antes de confirmar.',
          confirmButtonText: 'Entendido',
        });

        this.exchangeRate = currentRate;
        this.tasaCambioId = rateId;
        this.valorCambioId = rateId;
        const tasaFormateada = currentRate.toFixed(4);
        this.exchangeType = `1CLP=${tasaFormateada}VES`;
        this.confirmedExchangeRate = currentRate;

        this.isCalculatingFromSend = true;
        this.calculateReceiveAmount();

        this.currentStep = 1;
        this.isSubmitting = false;
      } else {
        this.tasaCambioId = rateId;
        this.valorCambioId = rateId;

        await this.submitTransfer();
        this.spinner.stop(spinnerRef);
      }
    } catch (error) {
      this.spinner.stop(spinnerRef);
      this.isSubmitting = false;

      Swal.fire({
        confirmButtonColor: '#1e5a3f',
        icon: 'error',
        title: 'Error',
        text: 'No se pudo validar la tasa de cambio. Por favor, intenta nuevamente.',
      });
    }
  }

  async submitTransfer(): Promise<void> {
    // 30/10/2026: el canal FIAT se vuelve a consultar justo antes de enviar.
    // Al entrar ya se miraba, pero un cierre del operador con la pantalla
    // abierta no frenaba la transferencia.
    if (!(await this.canalSigueAbierto())) return;

    const hasBeneficiary = this.isVeUser
      ? this.selectedVesBeneficiary !== null
      : this.selectedBeneficiary !== null;

    if (this.transferForm.valid && hasBeneficiary) {
      let spinnerRef = this.spinner.start();

      try {
        if (this.isVeUser) {
          const retiroDto = {
            userId: Number(this.user.usuarioId),
            totalMonedaOrigen: this.sendAmount,
            totalMonedaDestino: this.receiveAmount,
            cuentaBeneficiarioId: Number(this.selectedVesBeneficiary!.id),
            tasa: this.exchangeRate,
            tasaCambioId: this.tasaCambioId,
            valorCambioId: this.valorCambioId,
          };

          await firstValueFrom(this.walletVesClientService.retiroVesClp(retiroDto));
        } else {
          const transaccionDTO = {
            paisOrigen: 'CL',
            userId: this.user.usuarioId,
            totalMonedaOrigen: this.sendAmount,
            totalMonedaDestino: this.receiveAmount,
            monedaOrigen: 'CLP',
            monedaDestino: 'VES',
            paisDestino: 'VE',
            formaPago: 'WALLET',
            diferencia: null,
            tasa: this.exchangeRate,
            cuentaBeneficiarioId: this.selectedBeneficiary!.cuentaBeneficiarioID,
            tasaCambioId: this.tasaCambioId,
            valorCambioId: this.valorCambioId,
            tipoTransaccion: 'FIAT',
          };

          await this.executeTransactions([transaccionDTO]);
        }

        this.transactionCreatedAt = new Date();

        this.spinner.stop(spinnerRef);

        await Swal.fire({
          confirmButtonColor: '#1e5a3f',
          icon: 'success',
          title: 'Transacción creada',
          text: 'Gracias por confiar en nosotros, estaremos validando su pago.',
          confirmButtonText: 'Entendido',
        });

        this.currentStep = 4;
      } catch (error) {
        this.spinner.stop(spinnerRef);
        this.isSubmitting = false;

        await Swal.fire({
          confirmButtonColor: '#1e5a3f',
          icon: 'error',
          title: 'Error',
          text: 'No se pudo crear la transacción. Por favor, intenta nuevamente.',
        });
      }
    }
  }

  async executeTransactions(transacciones: any[]): Promise<any[]> {
    const promesas = transacciones.map((transaccion) =>
      this.transaccionService.saveTransaccion(transaccion),
    );
    return Promise.all(promesas);
  }

  formatCurrency(amount: number, currency: Currency): string {
    const safeAmount = Number(amount) || 0;
    return `${currency.symbol}${safeAmount
      .toFixed(2)
      .replace(/\d(?=(\d{3})+\.)/g, '$&,')}`;
  }

  canProceed(): boolean {
    // Gate defensivo: si el sobregiro está consumido (currentBalance<=0 en flow CLP),
    // no permitir avanzar aunque el resto de la validación pase. Backend ya rechaza
    // este caso vía regla waterfall; acá evitamos disparar el error innecesariamente.
    if (!this.isVeUser && this.currentBalance <= 0) {
      return false;
    }
    return this.validateCurrentStep();
  }

  getFlagPath(currency: Currency): string {
    return `assets/flags/${currency.flag}`;
  }

  formatAmountWithThousands(amount: number): string {
    return amount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  }

  isSendAmountExceedingMax(): boolean {
    return (
      typeof this.sendAmount === 'number' &&
      isFinite(this.sendAmount) &&
      !isNaN(this.sendAmount) &&
      this.sendAmount > this.maxSendAmount
    );
  }

  getSelectedBeneficiaryName(): string {
    if (!this.selectedBeneficiary) {
      return 'No seleccionado';
    }
    return `${this.selectedBeneficiary.nombre} ${this.selectedBeneficiary.apellidos}`;
  }

  getSelectedBeneficiaryBank(): string {
    if (!this.selectedBeneficiary || !this.selectedBeneficiary.banco) {
      return '';
    }
    return this.selectedBeneficiary.banco.nombre;
  }

  finishTransaction(): void {
    this.router.navigate(['/wallet']);
  }

  async shareTransaction(): Promise<void> {
    const hasBeneficiary = this.isVeUser
      ? this.selectedVesBeneficiary !== null
      : this.selectedBeneficiary !== null;
    if (!this.transactionCreatedAt || !hasBeneficiary) {
      await Swal.fire({
        confirmButtonColor: '#1e5a3f',
        icon: 'error',
        title: 'Error',
        text: 'No se pueden compartir los datos de la transacción.',
      });
      return;
    }

    const transactionData = this.formatTransactionData();

    try {
      await navigator.clipboard.writeText(transactionData);

      await Swal.fire({
        confirmButtonColor: '#1e5a3f',
        icon: 'success',
        title: 'Copiado',
        text: 'Los datos de la transacción han sido copiados al portapapeles.',
        timer: 2000,
        timerProgressBar: true,
      });
    } catch (error) {
      await Swal.fire({
        confirmButtonColor: '#1e5a3f',
        icon: 'error',
        title: 'Error',
        text: 'No se pudo copiar al portapapeles.',
      });
    }
  }

  formatTransactionData(): string {
    if (!this.transactionCreatedAt) return '';

    const date = new Date(this.transactionCreatedAt);
    const pad = (n: number) => n.toString().padStart(2, '0');

    const dateUTC = `${date.getUTCFullYear()}-${pad(
      date.getUTCMonth() + 1,
    )}-${pad(date.getUTCDate())} ${pad(date.getUTCHours())}:${pad(
      date.getUTCMinutes(),
    )}:${pad(date.getUTCSeconds())}`;

    if (this.isVeUser) {
      const alias = this.selectedVesBeneficiary?.alias || 'No disponible';
      const banco = this.selectedVesBeneficiary?.banco || 'No disponible';
      return `
    DATOS DE TRANSACCIÓN

⏰ Hora de creación: ${dateUTC}

💰 Monto enviado: ${this.sendAmount.toFixed(2)} VES

💵 Monto a recibir: ${this.receiveAmount.toFixed(2)} CLP

👥 Beneficiario: ${alias}

🏦 Banco: ${banco}
      `.trim();
    }

    const beneficiaryName = this.selectedBeneficiary
      ? `${this.selectedBeneficiary.nombre} ${this.selectedBeneficiary.apellidos}`
      : '';
    const beneficiaryBank =
      this.selectedBeneficiary?.banco?.nombre || 'No disponible';

    return `
    DATOS DE TRANSACCIÓN

⏰ Hora de creación: ${dateUTC}

💰 Monto enviado: ${this.sendAmount.toFixed(2)} CLP

💵 Monto a recibir: ${this.receiveAmount.toFixed(2)} VES

👥 Beneficiario: ${beneficiaryName}

🏦 Banco: ${beneficiaryBank}
    `.trim();
  }

  async validarEstado() {
    this.estadoOperaciones =
      await this.hourOperations.getEstadoOperacion('FIAT');
    this.estadoStopOperaciones =
      await this.hourOperations.getEstadoOperacion('FIAT_STOP');
    const message =
      'Hemos cerrado operaciones, muchas gracias por preferirnos.';

    if (!this.estadoOperaciones) {
      Swal.fire({
        confirmButtonColor: '#1e5a3f',
        title: 'Operación cerrada',
        text: message,
      });
      this.router.navigate(['/wallet']);
    }

    if (!this.estadoStopOperaciones) {
      Swal.fire({
        confirmButtonColor: '#1e5a3f',
        title: 'Operación Pausada',
        text: 'Por motivos operativos, el sistema estará en pausa durante 10 minutos.',
      });
      this.router.navigate(['/wallet']);
    }
  }

  /**
   * 30/10/2026: vuelve a consultar el canal antes de enviar. `validarEstado`
   * avisa y saca al cliente si está cerrado o en pausa. Si la consulta falla
   * no se bloquea, igual que al entrar.
   */
  private async canalSigueAbierto(): Promise<boolean> {
    try {
      await this.validarEstado();
    } catch {
      return true;
    }
    return !!this.estadoOperaciones && !!this.estadoStopOperaciones;
  }

  async obtenerSaldo() {
    if (this.user?.usuarioId) {
      try {
        if (this.isVeUser) {
          const response = await firstValueFrom(this.walletVesClientService.getMyBalance());
          this.currentBalance = response?.data?.balanceVes ?? 0;
          this.overdraftAvailable = 0;
        } else {
          const { balancePermitido, sobregiro } =
            await this.cartolaService.getSaldoCliente(this.user.usuarioId);
          this.currentBalance = balancePermitido ?? 0;
          this.overdraftAvailable = sobregiro ?? 0;
        }
      } catch (error) {}
    }
  }

  async obtenerParametrosPagoMovil(): Promise<void> {
    try {
      const [globalParam, banescoParam] = await Promise.all([
        this.parameterService.getParameter(ParametrosEnum.PAGO_MOVIL_GLOBAL),
        this.parameterService.getParameter(ParametrosEnum.PAGO_MOVIL_BANESCO),
      ]);
      this.pagoMovilGlobal = Number(globalParam?.valor) || 0;
      this.pagoMovilBanesco = Number(banescoParam?.valor) || 0;
      this.pagoMovilDisabled =
        this.pagoMovilGlobal === 0 || this.pagoMovilBanesco === 0;
    } catch (error) {
      this.pagoMovilGlobal = 0;
      this.pagoMovilBanesco = 0;
      this.pagoMovilDisabled = true;
    }

    // Límite de monto para Pago Móvil (maneja su propio default ante errores)
    this.pagoMovilMontoMax = await this.parameterService.getPagoMovilMontoMax();
  }

  /**
   * En el flujo CL→VES el beneficiario recibe VES, por lo que el límite de
   * Pago Móvil (en Bs) se compara contra receiveAmount, NO contra sendAmount (CLP).
   */
  get isPagoMovilBlockedByAmount(): boolean {
    return !this.isVeUser && this.receiveAmount > this.pagoMovilMontoMax;
  }

  /** Un beneficiario de Pago Móvil no se puede usar si el monto supera el límite. */
  isBeneficiaryPagoMovilBlocked(beneficiary: CuentaBeneficiario): boolean {
    return beneficiary?.movilPay === 1 && this.isPagoMovilBlockedByAmount;
  }

  /** Deselecciona un beneficiario de Pago Móvil si el monto pasó a superar el límite. */
  private clearBlockedPagoMovilSelection(): void {
    if (
      this.selectedBeneficiary &&
      this.isBeneficiaryPagoMovilBlocked(this.selectedBeneficiary)
    ) {
      this.selectedBeneficiary = null;
      this.transferForm.get('selectedBeneficiary')?.setValue(null);
    }
  }

  async obtenerBeneficiarios() {
    if (this.user?.usuarioId) {
      try {
        const allBeneficiaries = await this.accountRecipientService.getByUser(
          this.user.usuarioId,
        );
        this.beneficiaries =
          this.filtrarBeneficiariosPagoMovil(allBeneficiaries);
      } catch (error) {
        this.beneficiaries = [];
      }
    }
  }

  private filtrarBeneficiariosPagoMovil(
    beneficiaries: CuentaBeneficiario[],
  ): CuentaBeneficiario[] {
    // BANESCO=1, GLOBAL=1 → fully enabled, no filter
    if (this.pagoMovilBanesco === 1 && this.pagoMovilGlobal === 1) {
      return beneficiaries;
    }

    // BANESCO=1, GLOBAL=0 → only allow movilPay for Banesco (bancoID=1)
    if (this.pagoMovilBanesco === 1 && this.pagoMovilGlobal === 0) {
      return beneficiaries.filter(
        (b) => b.movilPay === 0 || (b.movilPay === 1 && b.bancoID === 1),
      );
    }

    // BANESCO=0 → only show non-movilPay beneficiaries
    return beneficiaries.filter((b) => b.movilPay === 0);
  }

  async obtenerTasaCambio(
    monto: number,
    isPolling: boolean = false,
  ): Promise<void> {
    let spinnerRef: any = null;

    try {
      if (!isPolling) {
        spinnerRef = this.spinner.start();
      }

      const previousRate = this.exchangeRate;

      if (this.isVeUser) {
        // VE: obtener tasa VES→CLP (pública, sin auth)
        const response = await firstValueFrom(
          this.mainRatesService.obtenerTasaVesClpPorMonto(monto, 'Client'),
        );

        if (response) {
          // tasaAplicada = VES/CLP (ej: 3.28 VES = 1 CLP)
          this.exchangeRate = response.tasaAplicada;
          this.tasaCambioId = response.id;
          this.valorCambioId = response.id;

          const tasaFormateada = response.tasaAplicada.toFixed(4);
          this.exchangeType = `1CLP=${tasaFormateada}VES`;
        }
      } else {
        // CL: obtener tasa CLP→VES (con auth)
        const response = await firstValueFrom(
          this.mainRatesService.obtenerTasaPorMonto(monto, 'Client'),
        );

        if (response) {
          this.exchangeRate = response.tasaClient;
          this.tasaCambioId = response.id;
          this.valorCambioId = response.id;

          const tasaFormateada = response.tasaClient.toFixed(4);
          this.exchangeType = `1CLP=${tasaFormateada}VES`;
        }
      }

      // Recalcular según la dirección de cálculo actual
      if (this.isCalculatingFromSend && this.currentStep !== 4) {
        if (isPolling && previousRate !== this.exchangeRate) {
          this.calculateReceiveAmount();
        } else if (!isPolling) {
          this.calculateReceiveAmount();
        }
      } else {
        if (
          isPolling &&
          previousRate !== this.exchangeRate &&
          this.currentStep !== 4
        ) {
          this.calculateSendAmountFromReceive();
        }
      }
    } catch (error) {
      if (!isPolling) {
        Swal.fire({
          confirmButtonColor: '#1e5a3f',
          icon: 'warning',
          title: 'Advertencia',
          text: 'No se pudo obtener la tasa de cambio actual. Se usará la tasa por defecto.',
          timer: 3000,
          timerProgressBar: true,
        });
      }
    } finally {
      if (spinnerRef !== null) {
        this.spinner.stop(spinnerRef);
      }
    }
  }

  private async cargarTasasCambio(): Promise<void> {
    try {
      if (this.isVeUser) {
        const response = await firstValueFrom(
          this.mainRatesService.obtenerTasasVesClpPublicas(),
        );
        if (response.success && response.data) {
          this.tasasCambio = {
            rangoA: {
              monto: '$10,000 - $99,999 CLP',
              client: response.data.rangoA?.client?.tasa || 0,
            },
            rangoB: {
              monto: '$100,000 - $209,999 CLP',
              client: response.data.rangoB?.client?.tasa || 0,
            },
            rangoC: {
              monto: '$210,000 - $5,000,000 CLP',
              client: response.data.rangoC?.client?.tasa || 0,
            },
          };
          // Las tasas se cargan pero el modal solo se abre con el botón "Ver tasas del día"
        }
      } else {
        const response = await firstValueFrom(
          this.mainRatesService.obtenerTasasPublicas(),
        );
        if (response.success && response.data) {
          this.tasasCambio = {
            rangoA: {
              monto: '$10,000 - $99,999 CLP',
              client: response.data.rangoA?.client?.tasa || 0,
            },
            rangoB: {
              monto: '$100,000 - $209,999 CLP',
              client: response.data.rangoB?.client?.tasa || 0,
            },
            rangoC: {
              monto: '$210,000 - $5,000,000 CLP',
              client: response.data.rangoC?.client?.tasa || 0,
            },
          };
          // Las tasas se cargan pero el modal solo se abre con el botón "Ver tasas del día"
        }
      }
    } catch (error) {}
  }

  closeRatesModal(): void {
    this.showRatesModal = false;
  }

  openRatesModal(): void {
    this.showRatesModal = true;
  }

  recargarSaldo(): void {
    if (this.isVeUser) {
      this.router.navigate(['/wallet/client/load-pays-usdt']);
    } else {
      this.router.navigate(['/wallet/client/rechargeAccount']);
    }
  }

  // ============================================================
  // VE-SPECIFIC METHODS
  // ============================================================

  private async obtenerTasaCambioInicial(): Promise<void> {
    try {
      // Usar monto VES referencial para obtener tasa inicial
      // 10000 CLP * ~3.28 ≈ 32800 VES
      const montoRef = 33000;
      const response = await firstValueFrom(
        this.mainRatesService.obtenerTasaVesClpPorMonto(montoRef, 'Client'),
      );
      if (response) {
        this.exchangeRate = response.tasaAplicada;
        this.tasaCambioId = response.id;
        this.valorCambioId = response.id;

        const tasaFormateada = response.tasaAplicada.toFixed(4);
        this.exchangeType = `1CLP=${tasaFormateada}VES`;

        // Calcular min/max VES basado en la tasa
        this.minSendAmount = Math.ceil(10000 * this.exchangeRate);
        this.maxSendAmount = Math.floor(5000000 * this.exchangeRate);
        this.sendAmount = this.minSendAmount;

        this.transferForm.get('sendAmount')?.setValue(this.sendAmount, { emitEvent: false });
        this.transferForm.get('sendAmount')?.setValidators([
          Validators.required,
          Validators.min(this.minSendAmount),
          Validators.max(this.maxSendAmount),
        ]);
        this.transferForm.get('sendAmount')?.updateValueAndValidity({ emitEvent: false });

        this.calculateReceiveAmount();
      }
    } catch (error) {}
  }

  async obtenerBeneficiariosCl(): Promise<void> {
    try {
      this.vesBeneficiaries = await this.withdrawAccountService.getWithdrawAccounts();
    } catch (error) {
      this.vesBeneficiaries = [];
    }
  }

  get filteredVesBeneficiaries(): CuentaRetiro[] {
    if (!this.searchTerm) {
      return this.vesBeneficiaries;
    }
    const term = this.searchTerm.toLowerCase();
    return this.vesBeneficiaries.filter(
      (b) =>
        b.alias?.toLowerCase().includes(term) ||
        b.banco?.toLowerCase().includes(term) ||
        b.numeroCuenta?.toLowerCase().includes(term),
    );
  }

  selectVesBeneficiary(cuenta: CuentaRetiro): void {
    this.selectedVesBeneficiary = cuenta;
    this.transferForm.get('selectedBeneficiary')?.setValue(cuenta);
  }

  openAddVesBeneficiaryDialog(): void {
    const isMobile = window.innerWidth < 768;
    const dialogRef = this.dialog.open(AddWithdrawAccountsComponent, {
      data: {
        type: 'CREATE',
        cuenta: null,
        preselectedCountry: 'CL',
      },
      maxWidth: isMobile ? '95vw' : '826px',
      minWidth: isMobile ? '90vw' : '800px',
      maxHeight: '90vh',
    });

    dialogRef.afterClosed().subscribe(async (result) => {
      if (result === true) {
        await this.obtenerBeneficiariosCl();
      }
    });
  }
}
